import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useCallback,
  useState,
  useMemo,
} from "react";
import { DenyingParty } from "../__generated__/graphql";

export type InitialAppealState = {
  pbmName?: string | null;
  denyingParty?: DenyingParty | null;
  otherDenyingPartyName?: string | null;
};

type InitialAppealContextValue = {
  initialAppealValues: InitialAppealState;
  setInitialAppealValues: (
    initiateAppealValues: Partial<InitialAppealState>,
  ) => void;
};

const InitialAppealContext = createContext<InitialAppealContextValue | null>(
  null,
);

export const InitialAppealProvider = ({ children }: PropsWithChildren) => {
  const [initialAppealValues, setInitialAppealStateValues] =
    useState<InitialAppealState>({});

  const setInitialAppealValues = useCallback(
    (updatedInitialAppealValues: Partial<InitialAppealState>) => {
      setInitialAppealStateValues((currentInitialAppealValues) => ({
        ...currentInitialAppealValues,
        ...updatedInitialAppealValues,
      }));
    },
    [setInitialAppealStateValues],
  );

  const value = useMemo(
    () => ({
      initialAppealValues,
      setInitialAppealValues,
    }),
    [initialAppealValues, setInitialAppealValues],
  );

  return (
    <InitialAppealContext.Provider value={value}>
      {children}
    </InitialAppealContext.Provider>
  );
};

export const useInitialAppeal = () => {
  const initialAppealContext = useContext(InitialAppealContext);

  if (initialAppealContext === null) {
    throw new Error(
      "useInitialAppeal must be used within an InitialAppealProvider",
    );
  }

  return initialAppealContext;
};
